import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Store, Scissors, Ruler, PenTool, Send } from "lucide-react";
import { toast } from "sonner";
import { storeApi, formatStoreError } from "@/lib/storefront";
import SEO from "@/components/storefront/SEO";
import { BRAND } from "@/lib/brand";

const HERO_IMG = "https://images.unsplash.com/photo-1771098206750-6be5aef4f503?w=2400&auto=format&fit=crop&q=80";

const BUSINESS_TYPES = [
  { value: "quilt_shop", label: "Quilt shop" },
  { value: "sewing_studio", label: "Sewing studio" },
  { value: "longarm_quilter", label: "Longarm quilter" },
  { value: "pattern_designer", label: "Pattern designer" },
  { value: "independent_maker", label: "Independent maker" },
  { value: "guild_or_teacher", label: "Quilt guild / Teacher" },
  { value: "other", label: "Other" },
];

const PERKS = [
  [Store, "Small-batch friendly", "Fair minimums built for local shops and studios — no pallet-sized commitments to get started."],
  [Scissors, "Staples that sell", "Rulers, mats, cutters, threads, and fat quarter bundles — the everyday items your customers restock."],
  [Ruler, "Honest trade pricing", "Clear wholesale terms shared upon approval. No games, no hidden tiers."],
  [PenTool, "A direct line", "You'll work with a real person who knows the craft — by email, within one business day."],
];

const EMPTY = {
  business_name: "", legal_name: "", contact_name: "", email: "", phone: "",
  business_type: "quilt_shop", ein: "", resale_certificate: "", website: "",
  years_in_business: "1-3", monthly_volume: "$250-$500", sales_channel: "online",
  heard_about: "", message: "",
  address: { street: "", city: "", state: "", zip: "" },
};

const YEARS_OPTIONS = [
  { value: "less-1", label: "Less than 1 year" },
  { value: "1-3", label: "1–3 years" },
  { value: "3-10", label: "3–10 years" },
  { value: "10-plus", label: "10+ years" },
];

const VOLUME_OPTIONS = [
  { value: "under-$250", label: "Under $250 / month" },
  { value: "$250-$500", label: "$250 – $500 / month" },
  { value: "$500-$1000", label: "$500 – $1,000 / month" },
  { value: "$1000-plus", label: "$1,000+ / month" },
];

const CHANNEL_OPTIONS = [
  { value: "brick_and_mortar", label: "Brick & mortar store" },
  { value: "online", label: "Online store / marketplace" },
  { value: "both", label: "Both retail & online" },
  { value: "services", label: "Services (longarm, classes, commissions)" },
];

export default function Wholesale() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setAddr = (k) => (e) => setForm({ ...form, address: { ...form.address, [k]: e.target.value } });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.email.includes("@")) {
      toast.error("Please fill in business name, contact name, and a valid email");
      return;
    }
    if (!form.address.city.trim() || !form.address.state.trim()) {
      toast.error("Please fill in your business city and state");
      return;
    }
    setSending(true);
    try {
      const { data } = await storeApi.post("/wholesale", form);
      toast.success(data.message || "Application received");
      setDone(true);
    } catch (err) {
      toast.error(formatStoreError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="wholesale-page">
      <SEO
        title="Wholesale"
        description={`Small-batch wholesale from ${BRAND.name} for quilt shops, sewing studios, longarm quilters, and pattern designers. Fair minimums, honest pricing, US-based support.`}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Bolts of quilting fabric in a shop" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#3d1a2c]/80" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70">Wholesale · B2B</div>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] text-white md:text-[56px]">
              Supply for the shops that <span className="font-display-soft italic">serve makers</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
              Quilt shops, sewing studios, longarm quilters, pattern designers, and independent makers — we built our wholesale program for you. Small batches, fair terms, and a team that actually sews.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a href="#apply" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-[#3d1a2c] transition-colors hover:bg-[#F1E3DC]" data-testid="wholesale-apply-cta">
                Apply for an account <ArrowRight size={15} />
              </a>
              <a href={`mailto:${BRAND.wholesaleEmail}`} className="text-sm text-white/85 underline underline-offset-4 hover:text-white" data-testid="wholesale-email-link">
                {BRAND.wholesaleEmail}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="border-b border-[#E6D4CC] bg-white py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="grid gap-8 md:grid-cols-4 md:gap-10">
            {PERKS.map(([Icon, title, body]) => (
              <div key={title} className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#F1E3DC] text-[#9D5C6C]">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-display text-xl leading-tight text-[#2B2127]">{title}</div>
                  <div className="mt-1.5 text-[15px] leading-relaxed text-[#3A2E33]">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS + FORM */}
      <section id="apply" className="bg-[#FAF6F1] py-16 md:py-24">
        <div className="mx-auto grid max-w-[1240px] gap-12 px-6 md:grid-cols-[1fr_1.2fr] md:gap-16 md:px-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#6B5158]">How it works</div>
            <h2 className="mt-2 font-display text-3xl leading-tight text-[#2B2127] md:text-[40px]">
              Three steps to a wholesale account
            </h2>
            <ol className="mt-8 space-y-6">
              {[
                ["01", "Tell us about your business", "Business details, address, and trade information — takes about five minutes. Providing an EIN and resale certificate speeds up approval."],
                ["02", "We verify and review", "Every application is reviewed personally. We verify business details and may request supporting documentation (e.g., a resale certificate)."],
                ["03", "You get trade terms", "Approved accounts receive wholesale pricing, minimums, and ordering details by email. Your account is flagged for trade pricing."],
              ].map(([n, label, body]) => (
                <li key={n} className="flex gap-4">
                  <div className="font-mono text-sm text-[#9D5C6C]">{n}</div>
                  <div>
                    <div className="font-display text-lg text-[#2B2127]">{label}</div>
                    <div className="mt-1 text-[14px] leading-relaxed text-[#6B5158]">{body}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-10 rounded-md border border-[#E6D4CC] bg-white p-5">
              <div className="text-xs uppercase tracking-[0.22em] text-[#6B5158]">Prefer email?</div>
              <a href={`mailto:${BRAND.wholesaleEmail}`} className="mt-1 block font-display text-lg text-[#2B2127] hover:text-[#9D5C6C]">{BRAND.wholesaleEmail}</a>
              <p className="mt-2 text-[13px] text-[#6B5158]">Some folks don't love forms — a plain email with your shop name and website works just as well.</p>
            </div>
            <div className="mt-4 rounded-md border border-[#E6D4CC] bg-white p-5" data-testid="line-sheet-box">
              <div className="text-xs uppercase tracking-[0.22em] text-[#6B5158]">For buyers</div>
              <Link to="/wholesale/line-sheet" className="mt-1 block font-display text-lg text-[#2B2127] hover:text-[#9D5C6C]">View the current line sheet →</Link>
              <p className="mt-2 text-[13px] text-[#6B5158]">SKUs, categories, and MSRP for our full assortment — printable. Trade pricing shared upon approval.</p>
            </div>
            <div className="mt-4 rounded-md border border-[#E6D4CC] bg-[#F1E3DC] p-5" data-testid="guilds-box">
              <div className="text-xs uppercase tracking-[0.22em] text-[#6D2E4B]">For guilds & teachers</div>
              <p className="mt-2 text-[14px] leading-relaxed text-[#3A2E33]">
                Run a quilt guild, teach a sewing class, or organize community workshops? We offer group pricing on
                staples for guild programs and classrooms. Apply with the form — choose{" "}
                <span className="font-semibold">"Quilt guild / Teacher"</span> as your business type and tell us about
                your group.
              </p>
            </div>
          </div>

          {done ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-[#E6D4CC] bg-white p-10 text-center md:p-14" data-testid="wholesale-success">
              <CheckCircle2 size={44} className="text-[#6D2E4B]" />
              <h3 className="mt-5 font-display text-2xl text-[#2B2127]">Application received</h3>
              <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-[#6B5158]">
                Thank you — we review every request personally and will reply to <span className="font-medium text-[#2B2127]">{form.email}</span> within 1–2 business days.
              </p>
              <Link to="/catalog" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-7 py-3 text-sm text-white hover:bg-[#59253d]">
                Browse the catalog <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-md border border-[#E6D4CC] bg-white p-6 md:p-10" data-testid="wholesale-form">
              <h3 className="font-display text-2xl text-[#2B2127]">Wholesale application</h3>
              <p className="mt-2 text-[13px] text-[#6B5158]">Fields marked * are required. Applications are reviewed manually — we reply within 1–2 business days.</p>

              <div className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6D2E4B]">Business details</div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <Field label="Business / store name (DBA) *" value={form.business_name} onChange={set("business_name")} placeholder="e.g. Sunset Quilt Studio" testId="wh-business-name" />
                <Field label="Legal entity name (if different)" value={form.legal_name} onChange={set("legal_name")} placeholder="e.g. Sunset Studio LLC" testId="wh-legal-name" />
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Business type *</label>
                  <select
                    value={form.business_type}
                    onChange={set("business_type")}
                    className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]"
                    data-testid="wh-business-type"
                  >
                    {BUSINESS_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Years in business *</label>
                  <select value={form.years_in_business} onChange={set("years_in_business")} className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="wh-years">
                    {YEARS_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Sales channel *</label>
                  <select value={form.sales_channel} onChange={set("sales_channel")} className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="wh-channel">
                    {CHANNEL_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <Field label="Website or social" value={form.website} onChange={set("website")} placeholder="yourshop.com or @yourshop" testId="wh-website" />
              </div>

              <div className="mt-7 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6D2E4B]">Business address</div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <Field label="Street address" value={form.address.street} onChange={setAddr("street")} placeholder="123 Main St, Suite 4" className="md:col-span-2" testId="wh-street" />
                <Field label="City *" value={form.address.city} onChange={setAddr("city")} placeholder="Miami" testId="wh-city" />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="State *" value={form.address.state} onChange={setAddr("state")} placeholder="FL" testId="wh-state" />
                  <Field label="ZIP" value={form.address.zip} onChange={setAddr("zip")} placeholder="33137" testId="wh-zip" />
                </div>
              </div>

              <div className="mt-7 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6D2E4B]">Trade details</div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <Field label="EIN / Federal Tax ID" value={form.ein} onChange={set("ein")} placeholder="12-3456789" testId="wh-ein" />
                <Field label="Resale certificate / Sales tax permit #" value={form.resale_certificate} onChange={set("resale_certificate")} placeholder="Optional at this stage" testId="wh-resale" />
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Estimated monthly volume *</label>
                  <select value={form.monthly_volume} onChange={set("monthly_volume")} className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="wh-volume">
                    {VOLUME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <Field label="How did you hear about us?" value={form.heard_about} onChange={set("heard_about")} placeholder="Instagram, a guild, a friend…" testId="wh-heard" />
              </div>

              <div className="mt-7 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6D2E4B]">Contact person</div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <Field label="Contact name *" value={form.contact_name} onChange={set("contact_name")} placeholder="Your full name" testId="wh-contact-name" />
                <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="you@yourshop.com" testId="wh-email" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="(305) 555-0100" testId="wh-phone" />
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Tell us about your business (optional)</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What do you make or sell? Which categories are you most interested in?"
                    className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]"
                    data-testid="wh-message"
                  />
                </div>
              </div>
              <button type="submit" disabled={sending} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-7 py-3.5 text-sm text-white transition-colors hover:bg-[#59253d] disabled:opacity-50" data-testid="wh-submit">
                {sending ? "Sending…" : <>Submit application <Send size={14} /></>}
              </button>
              <p className="mt-3 text-[12px] text-[#6B5158]">By applying you agree to our <Link to="/pages/terms" className="underline">terms</Link>. We never share your details — see the <Link to="/pages/privacy" className="underline">privacy policy</Link>.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", className = "", testId }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]"
        data-testid={testId}
      />
    </div>
  );
}
